import { lazy, ReactNode, Suspense } from 'react';

import styled from 'styled-components';

import { FiftyLogo } from '@components/icons/fifty-logo';
import { MobileLogoContainer } from '@components/page-containers/page-containers';
import { useMedia } from '@hooks/use-media';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import type { SigninSceneState } from '../signin-form-state';

const HalloweenScene = lazy(() => {
  return import('./halloween-scene');
});

const SCENE_MIN_WIDTH = 900;
/** The parlour wall (`HC.wall`), so the column already matches the scene while it loads. */
const PARLOUR_WALL = '#1C2925';
const HAS_ROOM_FOR_SCENE = `(min-width: ${SCENE_MIN_WIDTH}px)`;
const NO_ROOM_FOR_SCENE = `@media (max-width: ${SCENE_MIN_WIDTH - 1}px)`;

/**
 * The sign-in form on the left, the witches' interview parlour on the right. Narrow windows get
 * the form alone, and the scene isn't loaded at all.
 */
export const HalloweenSigninLayout = ({
  scene,
  children,
}: {
  scene: SigninSceneState;
  children: ReactNode;
}) => {
  const hasRoomForScene = useMedia(HAS_ROOM_FOR_SCENE);

  return (
    <Grid>
      {/* The form stays light even when the app is dark. */}
      <FormColumn className="light-theme" data-testid="signin-form-column">
        {!hasRoomForScene && (
          <MobileLogoContainer>
            <FiftyLogo size={80} color="indigo400" />
          </MobileLogoContainer>
        )}
        <FormContent>{children}</FormContent>
      </FormColumn>
      <SceneColumn data-testid="halloween-scene">
        {hasRoomForScene && (
          <Suspense fallback={null}>
            <HalloweenScene {...scene} />
          </Suspense>
        )}
      </SceneColumn>
    </Grid>
  );
};

const Grid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  height: '100vh',
  backgroundColor: theme.color.backgroundPrimary,
  [NO_ROOM_FOR_SCENE]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
});

const FormColumn = styled.div({
  position: 'relative',
  minWidth: 0,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  padding: `${pxToRem(48)} ${pxToRem(24)}`,
  boxSizing: 'border-box',
  backgroundColor: theme.color.backgroundPrimary,
  [NO_ROOM_FOR_SCENE]: {
    paddingTop: pxToRem(96),
  },
});

// Auto margins centre the form and still let it scroll from the top when it overflows.
const FormContent = styled.div({
  width: `min(${pxToRem(448)}, 100%)`,
  margin: 'auto',
});

const SceneColumn = styled.div({
  position: 'relative',
  minWidth: 0,
  overflow: 'hidden',
  backgroundColor: PARLOUR_WALL,
  [NO_ROOM_FOR_SCENE]: {
    display: 'none',
  },
});
